import { render, h } from "preact";
import { App } from "./App.js";
import { PhotoClient } from "./photo-client.js";
import { SHADOW_CSS } from "./styles.js";

interface MountConfig {
  containerId?: string;
  supabaseUrl: string;
  supabaseAnon: string;
  storageBucket?: string;
}

// Default Firebase Storage bucket for the church's project. Override via data-storage-bucket
// if the embed is ever deployed against a different Firebase project.
const DEFAULT_STORAGE_BUCKET = "campaignmediamgr.firebasestorage.app";

declare global {
  interface Window {
    CampaignMedia?: { mount: (cfg: MountConfig) => void };
  }
}

function mount(cfg: MountConfig) {
  const container = document.getElementById(cfg.containerId ?? "campaign-media");
  if (!container) {
    console.warn("[CampaignMedia] container not found");
    return;
  }
  const shadow = container.attachShadow({ mode: "closed" });
  const style = document.createElement("style");
  style.textContent = SHADOW_CSS;
  const root = document.createElement("div");
  shadow.appendChild(style);
  shadow.appendChild(root);
  const client = new PhotoClient({
    supabaseUrl: cfg.supabaseUrl,
    supabaseAnon: cfg.supabaseAnon,
    storageBucket: cfg.storageBucket ?? DEFAULT_STORAGE_BUCKET,
  });
  render(h(App, { client }), root);
}

function findOwnScript(): HTMLScriptElement | null {
  // For sync scripts, document.currentScript points at us.
  const curr = document.currentScript as HTMLScriptElement | null;
  if (curr && curr.src && curr.src.includes("embed.js")) return curr;
  // For async/defer scripts, currentScript is null. Find ourselves by src match.
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src*="embed.js"]');
  for (const s of scripts) {
    // Prefer one with our data attributes set
    if (s.dataset.supabaseUrl || s.dataset.supabaseAnon) return s;
  }
  return scripts[0] ?? null;
}

function readAttrs(el: HTMLElement | null): {
  supabaseUrl: string; supabaseAnon: string; storageBucket: string | undefined;
} | null {
  if (!el) return null;
  const supabaseUrl = (el.dataset.supabaseUrl ?? "").replace(/\s+/g, "");
  const supabaseAnon = (el.dataset.supabaseAnon ?? "").replace(/\s+/g, "");
  const storageBucket = (el.dataset.storageBucket ?? "").replace(/\s+/g, "") || undefined;
  if (!supabaseUrl || !supabaseAnon) return null;
  return { supabaseUrl, supabaseAnon, storageBucket };
}

// WP/KSES sanitizers strip data-* from arbitrary elements but leave script
// tag *contents* intact, so a JSON config block survives where data-attrs don't.
function readJsonConfig(): {
  supabaseUrl: string; supabaseAnon: string; storageBucket: string | undefined;
  containerId?: string;
} | null {
  const node = document.getElementById("campaign-media-config");
  if (!node || !node.textContent) return null;
  // Elementor/WP HTML editors sometimes word-wrap long string values, inserting
  // raw newlines that JSON disallows inside string literals. None of our config
  // values legitimately contain control characters, so strip them defensively.
  const cleaned = node.textContent.replace(/[ -]/g, "");
  try {
    const parsed = JSON.parse(cleaned);
    const supabaseUrl = String(parsed.supabaseUrl ?? "").replace(/\s+/g, "");
    const supabaseAnon = String(parsed.supabaseAnon ?? "").replace(/\s+/g, "");
    if (!supabaseUrl || !supabaseAnon) return null;
    const storageBucket = parsed.storageBucket
      ? String(parsed.storageBucket).replace(/\s+/g, "")
      : undefined;
    const containerId = parsed.containerId ? String(parsed.containerId) : undefined;
    return { supabaseUrl, supabaseAnon, storageBucket, containerId };
  } catch (err) {
    console.warn("[CampaignMedia] #campaign-media-config is not valid JSON", err);
    return null;
  }
}

function autoMount() {
  // 1. JSON config block — survives WP sanitization. Preferred path.
  const json = readJsonConfig();
  if (json) {
    const { containerId, ...attrs } = json;
    mount({ ...attrs, containerId });
    return;
  }
  // 2. data-* on the container div (works on permissive CMSes).
  const containerEl = document.getElementById("campaign-media");
  let attrs = readAttrs(containerEl);
  let containerId: string | undefined = containerEl ? "campaign-media" : undefined;
  // 3. data-* on the script tag (legacy).
  if (!attrs) {
    const script = findOwnScript();
    attrs = readAttrs(script);
    if (script?.dataset.container) containerId = script.dataset.container;
  }
  if (!attrs) {
    console.warn(
      "[CampaignMedia] missing config. Add <script type=\"application/json\" " +
      "id=\"campaign-media-config\">{\"supabaseUrl\":\"...\",\"supabaseAnon\":\"...\"}</script>."
    );
    return;
  }
  mount({ ...attrs, containerId });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoMount);
} else {
  autoMount();
}

window.CampaignMedia = { mount };
